// THIS IS AN AUTOGENERATED FILE. DO NOT EDIT THIS FILE DIRECTLY.

import { useMutation, useMutationState } from '@tanstack/react-query';
import { useHttpClient } from '@toggl/sync/http';
import type {
  AutomationsCreateAutomationRequest,
  AutomationsRunAutomationRequest,
  AutomationsUpdateAutomationRequest,
} from '../types';
import type { MutationOptions } from '../utils/mutations';
import {
  deleteAutomationsMutationKey,
  postAutomationsMutationKey,
  runAutomationsMutationKey,
  updateAutomationsMutationKey,
} from './keys';
import {
  deleteAutomations,
  postAutomations,
  runAutomations,
  updateAutomations,
} from './requests';

export type PostAutomationsMutationVariables = {
  workspace_id: number;
  opening_id: number;
  stage_id: number;
  data: AutomationsCreateAutomationRequest;
};

export type PostAutomationsMutationOptions<TContext = unknown> =
  MutationOptions<
    typeof postAutomations,
    PostAutomationsMutationVariables,
    TContext
  >;

/**
 * Creates an automation and returns it
 */
export function usePostAutomationsMutation<TContext = unknown>(
  options: PostAutomationsMutationOptions<TContext> = {}
) {
  const httpClient = useHttpClient();
  return useMutation({
    mutationFn: ({ workspace_id, opening_id, stage_id, data }) =>
      postAutomations(httpClient, workspace_id, opening_id, stage_id, data),
    mutationKey: postAutomationsMutationKey,
    ...options,
  });
}

/** Returns the variables of a pending postAutomations mutation. */
export function usePostAutomationsMutationVariables() {
  return useMutationState({
    filters: { mutationKey: postAutomationsMutationKey, status: 'pending' },
    select: (mutation) =>
      mutation.state.variables as PostAutomationsMutationVariables,
  });
}

export type RunAutomationsMutationVariables = {
  workspace_id: number;
  opening_id: number;
  stage_id: number;
  automation_id: number;
  data: AutomationsRunAutomationRequest;
};

export type RunAutomationsMutationOptions<TContext = unknown> = MutationOptions<
  typeof runAutomations,
  RunAutomationsMutationVariables,
  TContext
>;

/**
 * Run an automation
 */
export function useRunAutomationsMutation<TContext = unknown>(
  options: RunAutomationsMutationOptions<TContext> = {}
) {
  const httpClient = useHttpClient();
  return useMutation({
    mutationFn: ({ workspace_id, opening_id, stage_id, automation_id, data }) =>
      runAutomations(
        httpClient,
        workspace_id,
        opening_id,
        stage_id,
        automation_id,
        data
      ),
    mutationKey: runAutomationsMutationKey,
    ...options,
  });
}

/** Returns the variables of a pending runAutomations mutation. */
export function useRunAutomationsMutationVariables() {
  return useMutationState({
    filters: { mutationKey: runAutomationsMutationKey, status: 'pending' },
    select: (mutation) =>
      mutation.state.variables as RunAutomationsMutationVariables,
  });
}

export type DeleteAutomationsMutationVariables = {
  workspace_id: number;
  opening_id: number;
  stage_id: number;
  automation_id: number;
};

export type DeleteAutomationsMutationOptions<TContext = unknown> =
  MutationOptions<
    typeof deleteAutomations,
    DeleteAutomationsMutationVariables,
    TContext
  >;

/**
 * Deletes an automation and returns it
 */
export function useDeleteAutomationsMutation<TContext = unknown>(
  options: DeleteAutomationsMutationOptions<TContext> = {}
) {
  const httpClient = useHttpClient();
  return useMutation({
    mutationFn: ({ workspace_id, opening_id, stage_id, automation_id }) =>
      deleteAutomations(
        httpClient,
        workspace_id,
        opening_id,
        stage_id,
        automation_id
      ),
    mutationKey: deleteAutomationsMutationKey,
    ...options,
  });
}

/** Returns the variables of a pending deleteAutomations mutation. */
export function useDeleteAutomationsMutationVariables() {
  return useMutationState({
    filters: { mutationKey: deleteAutomationsMutationKey, status: 'pending' },
    select: (mutation) =>
      mutation.state.variables as DeleteAutomationsMutationVariables,
  });
}

export type UpdateAutomationsMutationVariables = {
  workspace_id: number;
  opening_id: number;
  stage_id: number;
  automation_id: number;
  data: Partial<AutomationsUpdateAutomationRequest>;
};

export type UpdateAutomationsMutationOptions<TContext = unknown> =
  MutationOptions<
    typeof updateAutomations,
    UpdateAutomationsMutationVariables,
    TContext
  >;

/**
 * Updates an automation and returns it
 */
export function useUpdateAutomationsMutation<TContext = unknown>(
  options: UpdateAutomationsMutationOptions<TContext> = {}
) {
  const httpClient = useHttpClient();
  return useMutation({
    mutationFn: ({ workspace_id, opening_id, stage_id, automation_id, data }) =>
      updateAutomations(
        httpClient,
        workspace_id,
        opening_id,
        stage_id,
        automation_id,
        data
      ),
    mutationKey: updateAutomationsMutationKey,
    ...options,
  });
}

/** Returns the variables of a pending updateAutomations mutation. */
export function useUpdateAutomationsMutationVariables() {
  return useMutationState({
    filters: { mutationKey: updateAutomationsMutationKey, status: 'pending' },
    select: (mutation) =>
      mutation.state.variables as UpdateAutomationsMutationVariables,
  });
}
